import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { GalleryActions } from '../../state/gallery.actions';
import { GalleryState } from '../../state/gallery.state';
import { Subject, Observable } from 'rxjs';
import { StudioOptionsModel } from '../../models/studio-options.model';
import { OrganizationsState } from 'src/app/organizations/state/organizations.state';
import { takeUntil, filter, debounceTime, tap, map, distinctUntilChanged } from 'rxjs/operators';
import { groupBy } from 'lodash';
import { FormGroup, FormBuilder, AbstractControl } from '@angular/forms';

@Component({
  selector: 'app-gallery-filters-studio',
  templateUrl: './gallery-filters-studio.component.html',
  styleUrls: ['./gallery-filters-studio.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryFiltersStudioComponent implements OnInit, OnDestroy {
  @Select(GalleryState.getStudioCategoryId)
  categoryId$: Observable<string>;

  @Select(GalleryState.getStudioQuery)
  query$: Observable<string>;

  constructor(
    private store: Store,
    private changeDetectorRef: ChangeDetectorRef,
    private formBuilder: FormBuilder
  ) { }

  ngUnsubscribe = new Subject();
  groupedOptions: any = {};
  category: StudioOptionsModel;
  form: FormGroup;

  get queryControl(): AbstractControl {
    return this.form.get('query');
  }

  get categoryControl(): AbstractControl {
    return this.form.get('category');
  }

  ngOnInit() {
    this.initForm();
    this.initFormListeners();
    this.populateDropdown();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initForm() {
    const category = this.store.selectSnapshot(GalleryState.getStudioCategoryId);
    const query = this.store.selectSnapshot(GalleryState.getStudioQuery);

    this.form = this.formBuilder.group({
      category: [category],
      query: [query]
    });
  }

  initFormListeners() {
    this.form.valueChanges
      .pipe(
        tap(() => {
          this.category = this.store.selectSnapshot(GalleryState.getStudioOptions).find(option => option.id === this.categoryControl.value);
          this.changeDetectorRef.markForCheck();
        }),
        map(value => JSON.stringify(value)),
        distinctUntilChanged(),
        map(value => JSON.parse(value)),
        debounceTime(300),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.store.dispatch(new GalleryActions.SearchStudio(this.category, this.queryControl.value));
      });
  }

  populateDropdown() {
    // Populate dropdown with organization collections, if available
    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
    this.store.dispatch(new GalleryActions.GetStudioOptions(organization ? organization.id : null));
    this.store.select(GalleryState.getStudioOptions)
      .pipe(
        filter(options => options.length > 0),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(options => {
        this.updateOptions(options);
      });
  }

  updateOptions(options: StudioOptionsModel[]) {
    this.groupedOptions = groupBy(options, (option) => {
      return option.type;
    });

    const category = this.store.selectSnapshot(GalleryState.getStudioCategoryId);
    this.category = options.find(option => option.id === category);
    this.categoryControl.setValue(this.category.id);

    this.changeDetectorRef.markForCheck();
  }
}
